import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import classnames from 'classnames';
import entityComposition from '../common/classes/entityComposition';
import { processAutoLogoutDuration, processAutoLogoutAlertThreshold } from '../../utils/autoLogoutConfig';
const LogoutCounter = _ref => {
  let _ref$lastActionTimest = _ref.lastActionTimestamp,
    lastActionTimestamp = _ref$lastActionTimest === void 0 ? null : _ref$lastActionTimest,
    _ref$user = _ref.user,
    user = _ref$user === void 0 ? null : _ref$user,
    _ref$loginActions = _ref.loginActions,
    loginActions = _ref$loginActions === void 0 ? null : _ref$loginActions,
    _ref$autoLogoutDurati = _ref.autoLogoutDuration,
    autoLogoutDuration = _ref$autoLogoutDurati === void 0 ? null : _ref$autoLogoutDurati,
    _ref$autoLogoutAlertT = _ref.autoLogoutAlertThreshold,
    autoLogoutAlertThreshold = _ref$autoLogoutAlertT === void 0 ? null : _ref$autoLogoutAlertT;
  const _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    timeRemaining = _useState2[0],
    setTimeRemaining = _useState2[1];
  const intervalRef = useRef(null);
  const logoutTriggeredRef = useRef(false);
  useEffect(() => {
    // Reset logout triggered flag when dependencies change
    logoutTriggeredRef.current = false;

    // Only show counter if user has auto-logout enabled
    const hasRequiredProps = (user === null || user === void 0 ? void 0 : user.logoutAfterIdleTime) && lastActionTimestamp && loginActions && autoLogoutDuration;
    if (!hasRequiredProps) {
      setTimeRemaining(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    const calculateTimeRemaining = () => {
      const elapsed = Date.now() - lastActionTimestamp;
      const remaining = Math.max(0, autoLogoutDuration - elapsed);
      setTimeRemaining(remaining);

      // If time has expired, trigger logout immediately
      // This ensures logout happens even if the setTimeout callback doesn't fire
      if (remaining === 0 && loginActions && !logoutTriggeredRef.current) {
        logoutTriggeredRef.current = true;
        loginActions.logoutUser();
      }
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    intervalRef.current = setInterval(calculateTimeRemaining, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [lastActionTimestamp, user === null || user === void 0 ? void 0 : user.logoutAfterIdleTime, loginActions, autoLogoutDuration]);

  // Don't render if user doesn't have auto-logout enabled, no time remaining,
  // or more than threshold left
  if (!(user !== null && user !== void 0 && user.logoutAfterIdleTime) || timeRemaining <= 0 || !autoLogoutAlertThreshold || timeRemaining > autoLogoutAlertThreshold) {
    return null;
  }
  const formatTime = milliseconds => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return "".concat(minutes.toString().padStart(2, '0'), ":").concat(seconds.toString().padStart(2, '0'));
  };
  const formattedTime = formatTime(timeRemaining);
  const handleStayOnline = () => {
    // Dispatch keepAlive to reset the timer (this will update lastAction.timestamp)
    loginActions.keepAlive();
  };
  const handleLogout = () => {
    // Trigger logout with auto-save of pending changes
    loginActions.logoutUserWithSave();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "tqc-toast-container t-toast-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tqc-toast-notification t-toast-notification"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tqc-toast-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-notification-general"
  }, /*#__PURE__*/React.createElement("span", {
    className: classnames('tqc-notification-info-header', 't-notification-header')
  }, "Are you still there?"), /*#__PURE__*/React.createElement("br", null), "You will be automatically logged out due to inactivity.", /*#__PURE__*/React.createElement("br", null), "Remaining: ".concat(formattedTime), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: handleStayOnline,
    className: classnames('t-notification-button-left', 't-nbl-label-stay-online')
  }, "Stay Online"), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    style: {
      float: 'right'
    },
    onClick: handleLogout,
    className: classnames('t-notification-button-right', 't-nbr-label-logout')
  }, "Save & Logout")))));
};
LogoutCounter.propTypes = {
  lastActionTimestamp: PropTypes.number,
  user: PropTypes.shape({
    logoutAfterIdleTime: PropTypes.bool
  }),
  loginActions: PropTypes.shape({
    logoutUser: PropTypes.func,
    logoutUserWithSave: PropTypes.func,
    keepAlive: PropTypes.func
  }),
  autoLogoutDuration: PropTypes.number,
  autoLogoutAlertThreshold: PropTypes.number
};
export default entityComposition(LogoutCounter, ['settings'], {
  lastActionTimestamp: state => {
    var _state$lastAction;
    return (_state$lastAction = state.lastAction) === null || _state$lastAction === void 0 ? void 0 : _state$lastAction.timestamp;
  },
  user: state => state.login.user,
  autoLogoutDuration: _ref2 => {
    var _settings$settings;
    let _ref2$settings = _ref2.settings,
      settings = _ref2$settings === void 0 ? {} : _ref2$settings;
    const rawValue = (_settings$settings = settings.settings) === null || _settings$settings === void 0 || (_settings$settings = _settings$settings.configuration) === null || _settings$settings === void 0 || (_settings$settings = _settings$settings.logoutProperties) === null || _settings$settings === void 0 ? void 0 : _settings$settings.autoLogoutDuration;
    return processAutoLogoutDuration(rawValue);
  },
  autoLogoutAlertThreshold: _ref3 => {
    var _settings$settings2;
    let _ref3$settings = _ref3.settings,
      settings = _ref3$settings === void 0 ? {} : _ref3$settings;
    const rawValue = (_settings$settings2 = settings.settings) === null || _settings$settings2 === void 0 || (_settings$settings2 = _settings$settings2.configuration) === null || _settings$settings2 === void 0 || (_settings$settings2 = _settings$settings2.logoutProperties) === null || _settings$settings2 === void 0 ? void 0 : _settings$settings2.autoLogoutAlertThreshold;
    return processAutoLogoutAlertThreshold(rawValue);
  }
}, ['login']);